import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, CircularProgress, Divider, FormControl, InputLabel,
  MenuItem, Paper, Select, Stack, TextField, Typography, Alert,
  InputAdornment, Grid,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { getCurrencySymbol } from '../../utils/currency';
import { getMasters } from '../../services/platform/masterService';
import { listZones } from '../../services/zoneService';
import api from '../../services/api';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';

interface PricingBlock {
  basePrice: string;
  baseDistance: string;
  pricePerKm: string;
  pricePerMin: string;
  freeWaitingTime: string;
  waitingChargePerMin: string;
  cancellationFeeAfterAccept: string;
  cancellationFeeAfterArrive: string;
  adminCommissionType: 'PERCENT' | 'FIXED';
  adminCommissionValue: string;
}

const EMPTY_BLOCK: PricingBlock = {
  basePrice: '',
  baseDistance: '',
  pricePerKm: '',
  pricePerMin: '',
  freeWaitingTime: '',
  waitingChargePerMin: '',
  cancellationFeeAfterAccept: '',
  cancellationFeeAfterArrive: '',
  adminCommissionType: 'PERCENT',
  adminCommissionValue: '',
};

interface PricingFieldConfig {
  key: keyof PricingBlock;
  label: string;
  unit?: string;
  money?: boolean;
}

const PRICING_FIELDS: PricingFieldConfig[] = [
  { key: 'basePrice', label: 'Base Price', money: true },
  { key: 'baseDistance', label: 'Base Distance', unit: 'km' },
  { key: 'pricePerKm', label: 'Price Per KM', money: true },
  { key: 'pricePerMin', label: 'Price Per Minute', money: true },
  { key: 'freeWaitingTime', label: 'Free Waiting Time', unit: 'min' },
  { key: 'waitingChargePerMin', label: 'Waiting Charge / min', money: true },
  { key: 'cancellationFeeAfterAccept', label: 'Cancellation Fee (after accept)', money: true },
  { key: 'cancellationFeeAfterArrive', label: 'Cancellation Fee (after arrive)', money: true },
];

function PricingSection({
  title,
  block,
  onChange,
  currency,
}: {
  title: string;
  block: PricingBlock;
  onChange: (updated: PricingBlock) => void;
  currency?: string;
}) {
  const set = (key: keyof PricingBlock, value: string) => onChange({ ...block, [key]: value });
  const symbol = getCurrencySymbol(currency);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>{title}</Typography>
      <Grid container spacing={2}>
        {PRICING_FIELDS.map(({ key, label, unit, money }) => {
          const adornment = money ? symbol : unit;
          return (
            <Grid item xs={12} sm={6} key={key}>
              <TextField
                fullWidth
                size="small"
                label={label}
                type="number"
                value={block[key]}
                onChange={(e) => set(key, e.target.value)}
                InputProps={adornment ? { startAdornment: <InputAdornment position="start">{adornment}</InputAdornment> } : undefined}
              />
            </Grid>
          );
        })}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Admin Commission Type</InputLabel>
            <Select
              value={block.adminCommissionType}
              label="Admin Commission Type"
              onChange={(e) => set('adminCommissionType', e.target.value as 'PERCENT' | 'FIXED')}
            >
              <MenuItem value="PERCENT">Percentage (%)</MenuItem>
              <MenuItem value="FIXED">{symbol ? `Fixed (${symbol})` : 'Fixed amount'}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Admin Commission Value"
            type="number"
            value={block.adminCommissionValue}
            onChange={(e) => set('adminCommissionValue', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {block.adminCommissionType === 'PERCENT' ? '%' : (symbol || '')}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default function ZonePricingPage() {
  const queryClient = useQueryClient();
  useTenantCurrency();
  const [zoneId, setZoneId] = useState('');
  const [vehicleTypeId, setVehicleTypeId] = useState('');
  const [rideNow, setRideNow] = useState<PricingBlock>({ ...EMPTY_BLOCK });
  const [rideLater, setRideLater] = useState<PricingBlock>({ ...EMPTY_BLOCK });
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const { data: zonesData } = useQuery({
    queryKey: ['zones-pricing-page'],
    queryFn: () => listZones({ limit: 100 }),
  });

  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicle-types-pricing-page'],
    queryFn: () => getMasters('vehicle_types', { limit: 100, page: 1 }),
  });

  const zones = zonesData?.rows || [];
  const vehicles = vehiclesData?.data || [];
  const selectedZone = zones.find((z: { _id: string; currency?: string }) => z._id === zoneId);
  const zoneCurrency = selectedZone?.currency;

  // Load existing pricing when zone + vehicle selected
  const { data: existingPricing, isLoading: loadingPricing, isError: loadPricingError } = useQuery({
    queryKey: ['zone-vehicle-pricing', zoneId, vehicleTypeId],
    queryFn: async () => {
      const res = await api.get('/transport/pricing', { params: { zoneId, vehicleTypeId } });
      return res.data?.data || null;
    },
    enabled: !!(zoneId && vehicleTypeId),
  });

  useEffect(() => {
    if (existingPricing) {
      setRideNow(existingPricing.rideNow || { ...EMPTY_BLOCK });
      setRideLater(existingPricing.rideLater || { ...EMPTY_BLOCK });
    } else if (zoneId && vehicleTypeId) {
      setRideNow({ ...EMPTY_BLOCK });
      setRideLater({ ...EMPTY_BLOCK });
    }
  }, [existingPricing, zoneId, vehicleTypeId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.post('/transport/pricing', {
        zoneId,
        vehicleTypeId,
        rideNow,
        rideLater,
      });
    },
    onSuccess: () => {
      setSaved(true);
      setSaveError('');
      queryClient.invalidateQueries({ queryKey: ['zone-vehicle-pricing', zoneId, vehicleTypeId] });
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: { response?: { data?: { message?: string } } }) => {
      setSaveError(err.response?.data?.message || 'Failed to save pricing');
    },
  });

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transport"
        title="Zone Pricing"
        subtitle="Configure RIDE NOW and RIDE LATER pricing per zone and vehicle type."
        gradient
      />

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Select Zone & Vehicle Type</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Zone</InputLabel>
            <Select value={zoneId} label="Zone" onChange={(e) => setZoneId(e.target.value)}>
              {zones.map((z: { _id: string; name: string; code: string }) => (
                <MenuItem key={z._id} value={z._id}>{z.name} ({z.code})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Vehicle Type</InputLabel>
            <Select value={vehicleTypeId} label="Vehicle Type" onChange={(e) => setVehicleTypeId(e.target.value)}>
              {vehicles.map((v: { _id: string; name: string }) => (
                <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {loadPricingError && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load pricing. Check zone has a transport service and try again.</Alert>
      )}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

      {!(zoneId && vehicleTypeId) && (
        <Alert severity="info">Select a zone and vehicle type to configure pricing.</Alert>
      )}

      {zoneId && vehicleTypeId && (
        loadingPricing ? (
          <Stack alignItems="center" py={4}><CircularProgress /></Stack>
        ) : (
          <Stack spacing={3}>
            {saved && <Alert severity="success">Pricing saved successfully.</Alert>}

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
              <PricingSection title="RIDE NOW Pricing" block={rideNow} onChange={setRideNow} currency={zoneCurrency} />
            </Paper>

            <Divider />

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
              <PricingSection title="RIDE LATER Pricing" block={rideLater} onChange={setRideLater} currency={zoneCurrency} />
            </Paper>

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                size="large"
                startIcon={<Save />}
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save Pricing'}
              </Button>
            </Stack>
          </Stack>
        )
      )}
    </PageShell>
  );
}
